/**
 * IdsValidationResultsModal — read-only viewer for buildingSMART
 * IfcTester output triggered by ``POST /api/audits/{auditId}/validate-ids``.
 *
 * Receives the full validate() dict shape from
 * ``services.ifctester_wrapper`` (lines 108-133):
 *
 *   {
 *     available:        true,
 *     schema_version:   "IFC4" | "IFC4X3" | ...,
 *     total_specs:      int,
 *     passed_specs:     int,
 *     failed_specs:     int,
 *     passed_elements:  int,
 *     failed_elements:  int,
 *     specifications: [
 *       {
 *         name:                  string,
 *         ifc_version:           string,
 *         status:                "passed" | "failed",
 *         applicability_count:   int,
 *         failed_entities:       [{global_id, ifc_class, reason}, ...],
 *         passed_entities_count: int,
 *       }, ...
 *     ]
 *   }
 *
 * Display-only — no actions beyond Close.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShieldCheck, ShieldAlert } from 'lucide-react';

export default function IdsValidationResultsModal({ open, onOpenChange, result }) {
  if (!result) return null;
  const specs = Array.isArray(result.specifications) ? result.specifications : [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col"
                     data-testid="ids-validation-results-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-accent" />
            IDS Validation Results
          </DialogTitle>
          <DialogDescription>
            Specs: <span className="font-semibold text-foreground">{result.total_specs ?? 0}</span>
            {' · '}
            Passed: <span className="font-semibold text-[hsl(var(--success))]">{result.passed_specs ?? 0}</span>
            {' · '}
            Failed: <span className="font-semibold text-[hsl(var(--destructive))]">{result.failed_specs ?? 0}</span>
            {typeof result.failed_elements === 'number' && (
              <> {' · '}
                {result.failed_elements.toLocaleString()} failed element{result.failed_elements === 1 ? '' : 's'}
                {' / '}
                {(result.passed_elements ?? 0).toLocaleString()} passed
              </>
            )}
            {result.schema_version && (
              <> {' · '}<span className="text-muted-foreground">schema {result.schema_version}</span></>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-1.5"
             data-testid="ids-validation-spec-list">
          {specs.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              No specifications were generated from this project's confirmed rules.
            </p>
          )}
          {specs.map((spec, idx) => {
            const failed = (spec.status || '').toLowerCase() === 'failed';
            const failedCount = Array.isArray(spec.failed_entities) ? spec.failed_entities.length : 0;
            return (
              <div
                key={`${spec.name}-${idx}`}
                className={`flex items-start justify-between gap-3 p-2.5 rounded border ${
                  failed
                    ? 'border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5'
                    : 'border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5'
                }`}
                data-testid={`ids-spec-row-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {spec.name || '(unnamed spec)'}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-0.5">
                    applicability: {spec.applicability_count ?? 0} elements
                    {' · '}
                    passed: {spec.passed_entities_count ?? 0}
                    {failedCount > 0 && (
                      <> {' · '}
                        <span className="text-[hsl(var(--destructive))] font-semibold">
                          failed: {failedCount}
                        </span>
                      </>
                    )}
                    {spec.ifc_version && (
                      <> {' · '}schema {spec.ifc_version}</>
                    )}
                  </p>
                </div>
                <Badge
                  className={`text-[10px] px-1.5 border ${
                    failed
                      ? 'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30'
                      : 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
                  }`}
                >
                  {failed
                    ? <><ShieldAlert className="h-3 w-3 mr-1" />fail</>
                    : <><ShieldCheck className="h-3 w-3 mr-1" />pass</>}
                </Badge>
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-muted-foreground italic">
          Results generated by buildingSMART IfcTester against this project's exported IDS
        </p>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="close-ids-validation-modal-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
